// src/contexts/AuthContext.js (Enhanced with context switching)
import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthService } from '../services/authService';
import { InstitutionService } from '../services/institutionService';
import { ProjectService } from '../services/projectService';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';
import { db, storage, auth } from '../services/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userContexts, setUserContexts] = useState([]);
  const [activeContext, setActiveContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = AuthService.onAuthStateChanged(async (user) => {
      try {
        setCurrentUser(user);
        
        if (user) {
          const profile = await AuthService.getUserProfile(user.uid);
          setUserProfile(profile);
          
          if (profile && profile.profileCompleted) {
            await loadUserContexts(user.uid, profile); // 👈 Pass profile here
          } else {
            console.log('⚠️ Profile not completed yet. Skipping context loading.');
          }
        } else {
          console.log('❌ No user. Clearing contexts.');
          setUserProfile(null);
          setUserContexts([]);
          setActiveContext(null);
        }
      } catch (error) {
        console.error('💥 Error in auth state change:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const createMissingContextsFromProfile = async (userId, userProfile) => {
    try {
      const createdContexts = [];
      
      // Create institution document if user has school info
      if (userProfile.schoolName) {
        console.log('🏫 Creating institution for:', userProfile.schoolName);
        
        const institutionData = {
          type: 'School',
          schoolName: userProfile.schoolName,
          schoolPhase: userProfile.schoolPhase,
          district: userProfile.district,
          province: userProfile.province,
          circuit: userProfile.circuit,
          createdBy: userId,
          members: [userId], // Add user to members array
          createdAt: new Date(),
          updatedAt: new Date()
        };
        
        const institutionDoc = await addDoc(collection(db, 'taskdata-institutions'), institutionData);
        console.log('✅ Created institution document:', institutionDoc.id);
        createdContexts.push('institution');
      }
      
      // Create project document if user has project info  
      if (userProfile.projectName) {
        console.log('📋 Creating project for:', userProfile.projectName);
        
        const projectData = {
          name: userProfile.projectName,
          description: `Educational project: ${userProfile.projectName}`,
          type: 'Educational Project',
          district: userProfile.district,
          province: userProfile.province,
          status: 'active',
          createdBy: userId,
          members: [userId], // Add user to members array
          createdAt: new Date(),
          updatedAt: new Date(),
          // Add other relevant fields
          grades: userProfile.grades || [],
          subjects: userProfile.subjects || []
        };
        
        const projectDoc = await addDoc(collection(db, 'taskdata-projects'), projectData);
        createdContexts.push('project');
      }
      
      return createdContexts;
      
    } catch (error) {
      console.error('💥 Error creating contexts from profile:', error);
      throw error;
    }
  };

  const loadUserContexts = async (userId, profile) => {
    try {
      console.log('🔄 Loading user contexts for userId:', userId);
      console.log('👤 User profile passed:', profile);
      
      // Load user's institutions and projects
      const [institutions, projects] = await Promise.all([
        InstitutionService.getUserInstitutions(),
        ProjectService.getUserProjects()
      ]);

      console.log('🏫 Loaded institutions:', institutions);
      console.log('📋 Loaded projects:', projects);

      // Check if we need to create missing contexts from profile
      if (institutions.length === 0 && projects.length === 0 && profile) {
        console.log('⚠️ No contexts found but user has profile. Creating missing contexts...');
        console.log('📋 Profile data:', {
          schoolName: profile.schoolName,
          projectName: profile.projectName,
          institution: profile.institution
        });
        
        const created = await createMissingContextsFromProfile(userId, profile);
        
        if (created.length > 0) {
          console.log('✅ Created contexts:', created);
          // Reload contexts after creation
          await loadUserContexts(userId, profile);
          return;
        }
      }

      // Create contexts array from loaded data
      const contexts = [];

      // Add institution contexts
      institutions.forEach(institution => {
        console.log('🏫 Processing institution:', institution);
        contexts.push({
          id: institution.id,
          type: 'institution',
          name: institution.schoolName || institution.name,
          subtitle: `${institution.type} • ${institution.district}`,
          institutionType: institution.type,
          location: `${institution.district}, ${institution.province}`,
          data: institution,
          role: getUserRoleInInstitution(institution, userId)
        });
      });

      // Add project contexts
      projects.forEach(project => {
        console.log('📋 Processing project:', project);
        contexts.push({
          id: project.id,
          type: 'project',
          name: project.name,
          subtitle: `Project • ${project.district}`,
          institutionType: 'Project',
          location: `${project.district}, ${project.province}`,
          data: project,
          role: getUserRoleInProject(project, userId),
          status: project.status
        });
      });

      console.log('✅ Final contexts array:', contexts);
      setUserContexts(contexts);

      // Set active context based on user's primary institution type
      const storedContextId = localStorage.getItem(`activeContext_${userId}`);
      const storedContext = contexts.find(ctx => ctx.id === storedContextId);
      
      if (storedContext) {
        setActiveContext(storedContext);
      } else if (contexts.length > 0) {
        // Prefer project context if user's institution is "Project"
        const preferredContext = profile?.institution === 'Project' 
          ? contexts.find(ctx => ctx.type === 'project') || contexts[0]
          : contexts.find(ctx => ctx.type === 'institution') || contexts[0];
          
        setActiveContext(preferredContext);
        localStorage.setItem(`activeContext_${userId}`, preferredContext.id);
      } else {
        console.log('⚠️ No contexts available to set as active');
      }

    } catch (error) {
      console.error('💥 Error loading user contexts:', error);
    }
  };

  const getUserRoleInInstitution = (institution, userId) => {
    // Get user's role from the institution data or user profile
    if (institution.createdBy === userId) return 'Creator';
    return userProfile?.position || 'Member';
  };

  const getUserRoleInProject = (project, userId) => {
    // Get user's role from the project data or user profile
    if (project.createdBy === userId) return 'Creator';
    return userProfile?.position || 'Participant';
  };

  const switchContext = async (contextId) => {
    const context = userContexts.find(ctx => ctx.id === contextId);
    if (context) {
      setActiveContext(context);
      localStorage.setItem(`activeContext_${currentUser.uid}`, contextId);
    }
  };

  const refreshContexts = async () => {
    if (currentUser && userProfile) {
      await loadUserContexts(currentUser.uid, userProfile);
    }
  };

  const register = async (email, password, displayName) => {
    setError(null);
    try {
      const user = await AuthService.registerWithEmail(email, password, displayName);
      return user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const signIn = async (email, password) => {
    setError(null);
    try {
      const user = await AuthService.signInWithEmail(email, password);
      return user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    setError(null);
    try {
      const user = await AuthService.signInWithGoogle();
      return user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const sendPasswordReset = async (email) => {
    setError(null);
    try {
      return await AuthService.sendPasswordReset(email);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      // Clear active context
      setActiveContext(null);
      setUserContexts([]);
      if (currentUser) {
        localStorage.removeItem(`activeContext_${currentUser.uid}`);
      }
      await AuthService.signOut();
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const updateProfile = async (profileData) => {
    setError(null);
    try {
      if (currentUser) {
        await AuthService.updateUserProfile(currentUser.uid, profileData);
        const updatedProfile = await AuthService.getUserProfile(currentUser.uid);
        setUserProfile(updatedProfile);
        
        // Refresh contexts as profile update might affect them
        await loadUserContexts(currentUser.uid);
        
        return updatedProfile;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const migrateUser = async () => {
    if (currentUser) {
      try {
        await AuthService.migrateExistingUser(currentUser);
        const profile = await AuthService.getUserProfile(currentUser.uid);
        setUserProfile(profile);
      } catch (error) {
        console.error('Error migrating user:', error);
      }
    }
  };

  const value = {
    currentUser,
    userProfile,
    userContexts,
    activeContext,
    loading,
    error,
    register,
    signIn,
    signInWithGoogle,
    sendPasswordReset,
    signOut,
    updateProfile,
    migrateUser,
    switchContext,
    refreshContexts,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};